﻿import { QueryClient, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import {
  ALL_USERS_QUERY_KEY,
  CURRENT_USER_QUERY_KEY,
  SAS_TOKEN_PROFILE_PICTURE_QUERY_KEY,
  USER_API_URL,
  USER_BY_ID_QUERY_KEY,
  USER_INVITATION_LINK,
  USER_INVITATION_OPTIONS,
  USER_QUERY_KEY
} from '@/constants';
import { apiUrl, getQueryParams } from '@/helpers';
import {
  ErrorResponse,
  ForgotPasswordCredentials,
  InvitationOptions,
  Options,
  SasTokenPayload,
  SetPasswordForm,
  UpdatePersonalInformation,
  UpdateUserBasic,
  UpdateUserLanguage,
  UploadProfilPicture,
  User
} from '@/types';

/** Separate instance so AuthProvider's Bearer interceptor is not applied. */
const anonymousApi = axios.create();

export const endpoints = {
  getCurrentUser: () => axios.get(`${apiUrl()}${USER_API_URL}`),
  getAllUsers: (
    pageSize: number,
    cursor: number,
    searchQuery?: string,
    roleId?: number,
    enabled?: boolean,
    organizationId?: number,
    includeSelf?: boolean
  ) => {
    return axios.get(
      `${apiUrl()}${USER_API_URL}/all${getQueryParams({
        'paging.PageSize': pageSize as number,
        'paging.Cursor': cursor as number,
        'search.Text': searchQuery as string,
        'filter.RoleId': roleId as number,
        'filter.Enabled': enabled as boolean,
        'filter.Organizations[0].Id': organizationId as number,
        'filter.IncludeSelf': includeSelf as boolean
      })}`
    );
  },

  createUser: (payload: User) => axios.post(`${apiUrl()}${USER_API_URL}`, payload),
  createUploaderUser: (payload: User) =>
    axios.post(`${apiUrl()}${USER_API_URL}/create_uploader`, payload),
  updateUser: (payload: User) => axios.put(`${apiUrl()}${USER_API_URL}`, payload),
  updatePersonalInformation: (payload: UpdatePersonalInformation) =>
    axios.patch(`${apiUrl()}${USER_API_URL}/personal_information`, payload),
  confirmEmailChange: (token: string) =>
    axios.post(`${apiUrl()}${USER_API_URL}/confirm_email_change?token=${token}`),
  updateUploaderUser: (payload: UpdateUserBasic) =>
    axios.patch(`${apiUrl()}${USER_API_URL}/update_uploader`, payload),
  getUserById: (id: number) => axios.get(`${apiUrl()}${USER_API_URL}/${id}`),
  deleteUser: (id: number) => axios.delete(`${apiUrl()}${USER_API_URL}/${id}`),
  deleteUploaderUser: (id: number) =>
    axios.delete(`${apiUrl()}${USER_API_URL}/delete_uploader/${id}`),
  setPassword: ({ token, payload }: { token: string; payload: SetPasswordForm }) =>
    anonymousApi.post(
      `${apiUrl()}${USER_API_URL}/set_password?token=${encodeURIComponent(token)}`,
      payload
    ),
  getInvitationOptions: (token: string) =>
    anonymousApi.get<InvitationOptions>(
      `${apiUrl()}${USER_API_URL}/invitation?token=${encodeURIComponent(token)}`
    ),
  updateUserLanguage: (payload: UpdateUserLanguage) =>
    axios.patch(`${apiUrl()}${USER_API_URL}`, payload),
  uploadProfilePicture: (payload: UploadProfilPicture) =>
    axios.patch(`${apiUrl()}${USER_API_URL}/uploadProfilePicture`, payload),
  sasTokenProfilePicture: (payload: SasTokenPayload) =>
    axios.post(`${apiUrl()}${USER_API_URL}/sasToken`, payload),
  getUserInvitationLink: (id: number) =>
    axios.get(`${apiUrl()}${USER_API_URL}/invitationLink/${id}`),
  forgotPassword: (payload: ForgotPasswordCredentials) =>
    axios.post(`${apiUrl()}${USER_API_URL}/forgot_password`, payload),
  resetMfa: (id: number) => axios.post(`${apiUrl()}${USER_API_URL}/${id}/reset_mfa`),
  updateMfaStatus: (payload: { isMfaEnabled: boolean }) =>
    axios.patch(`${apiUrl()}${USER_API_URL}/mfa_status`, payload),
  linkSso: (payload: { providerId: string; idToken?: string; accessToken?: string }) =>
    axios.post(`${apiUrl()}${USER_API_URL}/link_sso`, payload)
};

export const useGetCurrentUserQuery = (loggedIn?: boolean) => {
  return useQuery([CURRENT_USER_QUERY_KEY, loggedIn], endpoints.getCurrentUser, {
    enabled: loggedIn && navigator.onLine,
    cacheTime: 1000,
    retry: false
  });
};

export const useGetAllUsers = (
  pageSize: number,
  searchQuery?: string,
  filterByRole?: number,
  filterByOrganizationId?: number,
  enabledFilter?: boolean,
  enabledQuery = true,
  includeSelf?: boolean
) => {
  return useInfiniteQuery(
    [
      ALL_USERS_QUERY_KEY,
      { pageSize, searchQuery, filterByRole, enabledFilter, filterByOrganizationId }
    ],
    ({ pageParam }) =>
      endpoints.getAllUsers(
        pageSize,
        pageParam,
        searchQuery,
        filterByRole,
        enabledFilter,
        filterByOrganizationId,
        includeSelf
      ),
    {
      enabled: enabledQuery,
      keepPreviousData: true,
      refetchOnMount: 'always',
      cacheTime: 0,
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.data?.nextCursor !== undefined) {
          return lastPage.data?.nextCursor;
        }
        return undefined;
      }
    }
  );
};

export const useGetUserByIdQuery = (id: number, options?: Options) => {
  return useQuery([USER_BY_ID_QUERY_KEY, { id }], () => endpoints.getUserById(id), {
    enabled: !!id,
    retry: false,
    onSuccess: () => {
      options?.onSuccess();
    },
    onError: (error) => {
      options?.onError(error as ErrorResponse);
    }
  });
};

export const useCreateUserMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(endpoints.createUser, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_USERS_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useCreateUploaderUserMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(endpoints.createUploaderUser, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_USERS_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateUserMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(endpoints.updateUser, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_USERS_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdatePersonalInformationMutation = (
  queryClient: QueryClient,
  options: Options
) => {
  return useMutation(endpoints.updatePersonalInformation, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([CURRENT_USER_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useConfirmEmailChangeMutation = (options: Options) => {
  return useMutation(endpoints.confirmEmailChange, {
    onSuccess: () => {
      options.onSuccess();
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateUploaderUserMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(endpoints.updateUploaderUser, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_USERS_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useDeleteUserMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(endpoints.deleteUser, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_USERS_QUERY_KEY]);
    },
    onError: (error) => {
      queryClient.invalidateQueries([ALL_USERS_QUERY_KEY]);
      options.onError?.(error as ErrorResponse);
    }
  });
};

export const useDeleteUploaderUserMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(endpoints.deleteUploaderUser, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_USERS_QUERY_KEY]);
    }
  });
};

export const useGetInvitationOptionsQuery = (token: string, enabled = true) => {
  return useQuery([USER_INVITATION_OPTIONS, token], () => endpoints.getInvitationOptions(token), {
    enabled: Boolean(token) && enabled,
    retry: false,
    refetchOnWindowFocus: false
  });
};

export const useSetPasswordMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(endpoints.setPassword, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([USER_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateUserLanguageMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(endpoints.updateUserLanguage, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_USERS_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUploadProfilePictureMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(endpoints.uploadProfilePicture, {
    onSuccess: async (data) => {
      await queryClient.cancelQueries([CURRENT_USER_QUERY_KEY]);
      options.onSuccess(data);
      queryClient.invalidateQueries([CURRENT_USER_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useSasTokenProfilePicture = (queryClient: QueryClient, options: Options) => {
  return useMutation(endpoints.sasTokenProfilePicture, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([SAS_TOKEN_PROFILE_PICTURE_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useGetUserInvitationLinkQuery = (id: number, options?: Options, enabled?: boolean) => {
  return useQuery([USER_INVITATION_LINK, { id }], () => endpoints.getUserInvitationLink(id), {
    retry: false,
    enabled: enabled ?? true,
    onSuccess: () => {
      options?.onSuccess();
    },
    onError: (error) => {
      options?.onError(error as ErrorResponse);
    }
  });
};

export const useForgotPasswordMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(endpoints.forgotPassword, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([USER_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useResetMfaMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(endpoints.resetMfa, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_USERS_QUERY_KEY]);
      queryClient.invalidateQueries([USER_BY_ID_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateMfaStatusMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(endpoints.updateMfaStatus, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([CURRENT_USER_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};
